// The browser's own reads of the public ledger's mirror, for the re-sign check in public-ledger-mirror.ts. Straight from the
// browser to the public mirror, never through nodeStream, so nodeStream's servers can't change what they answer. Only
// public account and schedule ids are sent.
import {
  MIRROR_HOSTS,
  operatingAccountFromHistory,
  type MirrorAllowance,
  type MirrorSchedule,
  type MirrorTransaction,
  type OperatingAnchor,
} from './public-ledger-mirror.js';
import { isLedgerNetwork } from './public-ledger-network.js';

type Fetch = (url: string) => Promise<{ ok: boolean; status: number; json(): Promise<unknown> }>;

const ACCOUNT_ID = /^\d+\.\d+\.\d+$/;

async function readJson(fetcher: Fetch, url: string): Promise<unknown> {
  const response = await fetcher(url);
  if (response.status === 404) return null;
  if (!response.ok) throw new Error("The public ledger's record can't be read right now. Please try again shortly.");
  return response.json();
}

function listOf<T>(value: unknown, key: string): T[] {
  const list = value && typeof value === 'object' ? (value as Record<string, unknown>)[key] : null;
  return Array.isArray(list) ? (list as T[]) : [];
}

function hostOf(network: string | null | undefined): string {
  if (!isLedgerNetwork(network)) throw new Error("Your company's public ledger network is not known.");
  return MIRROR_HOSTS[network];
}

/**
 * The company's operating account from its public ledger history: the latest 100 successful transfers out of the company
 * account (`type=debit`, so credit deliveries into it don't crowd out its top-ups), the allowances it has granted, and the
 * schedule of its last applied approval when one is named (counted only if the mirror shows the company's rule ran it),
 * read from the mirror (operatingAccountFromHistory decides).
 */
export async function readOperatingAnchor(
  network: string | null | undefined,
  companyAccountId: string | null | undefined,
  fetcher: Fetch = (url) => fetch(url),
  approvalScheduleId: string | null = null,
): Promise<OperatingAnchor> {
  if (!companyAccountId || !ACCOUNT_ID.test(companyAccountId)) {
    return operatingAccountFromHistory({ companyAccountId, transactions: [], allowances: [] });
  }
  const host = hostOf(network);
  const account = encodeURIComponent(companyAccountId);
  const [transactions, allowances, approval] = await Promise.all([
    readJson(
      fetcher,
      `${host}/api/v1/transactions?account.id=${account}&transactiontype=CRYPTOTRANSFER&type=debit&result=success&order=desc&limit=100`,
    ),
    readJson(fetcher, `${host}/api/v1/accounts/${account}/allowances/crypto?limit=100`),
    approvalScheduleId ? readMirrorSchedule(network, approvalScheduleId, fetcher) : Promise.resolve(null),
  ]);
  return operatingAccountFromHistory({
    companyAccountId,
    transactions: listOf<MirrorTransaction>(transactions, 'transactions'),
    allowances: listOf<MirrorAllowance>(allowances, 'allowances'),
    approvals: [approval],
  });
}

// A schedule as the mirror holds it, or null when the mirror doesn't have it (yet)
export async function readMirrorSchedule(
  network: string | null | undefined,
  scheduleId: string,
  fetcher: Fetch = (url) => fetch(url),
): Promise<MirrorSchedule | null> {
  if (!ACCOUNT_ID.test(scheduleId)) return null;
  const found = await readJson(fetcher, `${hostOf(network)}/api/v1/schedules/${encodeURIComponent(scheduleId)}`);
  return found && typeof found === 'object' ? (found as MirrorSchedule) : null;
}
